import { getSdk } from "../../services/sdkService";
import { i18n } from "../../services/i18nService";
import { config } from "../../config/config";
import { getAppState } from "../../state/index";
import { getLogger } from "../../utils/logger";

const Logger = getLogger();
const PLATFORM = "lg";
function getPlayerDefaultErrorMessage() {
  const online = !!getAppState().isOnline;
  return i18n.get(online ? "pages.player.errors.playbackError" : "global.errors.internetConnectivity.text");
}

function getDrmErrorText(code) {
  return i18n.get("pages.player.errors.drmError", code);
}

/**
 * Returns a set of player error strings in relation to their error codes (currently from AVPlay Tizen's player).
 */
function getPlayerErrorMessages() {
  const online = !!getAppState().isOnline;
  const playerDefaultErrorMessage = getPlayerDefaultErrorMessage();
  return {
    "571115": `${playerDefaultErrorMessage} (571115)`,
    "571116": `${playerDefaultErrorMessage} (571116)`,
    "571118": `${playerDefaultErrorMessage} (571118)`,
    "571120": `${playerDefaultErrorMessage} (571120)`,
    "571121": i18n.get(online ? "pages.player.errors.timeout.text" : "global.errors.internetConnectivity.text"),
    "571122": online ? getDrmErrorText("571122") : i18n.get("global.errors.internetConnectivity.text"),
    "571123": `${playerDefaultErrorMessage} (571123)`,
    "571124": `${playerDefaultErrorMessage} (571124)`,
    "571125": `${playerDefaultErrorMessage} (571125)`,
    "571126": `${playerDefaultErrorMessage} (571126)`,
    "571127": `${playerDefaultErrorMessage} (571127)`,
    "571128": `${playerDefaultErrorMessage} (571128)`,
    "571129": `${playerDefaultErrorMessage} (571129)`,
    "571130": `${playerDefaultErrorMessage} (571130)`,
    "571131": `${playerDefaultErrorMessage} (571131)`,
    "571132": `${playerDefaultErrorMessage} (571132)`,
    "571133": `${playerDefaultErrorMessage} (571133)`,
    "571134": `${playerDefaultErrorMessage} (571134)`,
    "571135": `${playerDefaultErrorMessage} (571135)`,
    "571136": `${playerDefaultErrorMessage} (571136)`,
    "571137": `${playerDefaultErrorMessage} (571137)`,
    "571140": `${i18n.get("pages.player.errors.concurrencyError")} (571140)`,
    "571141": `${playerDefaultErrorMessage} (571141)`,
    "571142": getDrmErrorText("571142"),
    "571143": `${playerDefaultErrorMessage} (571143)`,
    "571144": `${playerDefaultErrorMessage} (571144)`,
    "571145": `${i18n.get("global.errors.timeout.text")} (571145)`,
    "571199": `${playerDefaultErrorMessage} (571199)`
  };
}

/**
 * Returns a map of texts to show in the header of the popup error
 */
function getPlayerErrorsMapMessagesHeading() {
  return {
    "571124": i18n.get("global.errors.internetConnectivity.title"),
    "571145": i18n.get("global.errors.internetConnectivity.title"),
    "571140": i18n.get("pages.player.errorTitle.concurrencyErrorTitle")
  };
}

/**
 * Returns the text to show in the header of the popup error
 * @param {String} errorCode Code of the error
 */
export function getPlayerErrorMessageHeading(errorCode) {
  if (!errorCode || !errorCode.includes) {
    return undefined;
  }
  const playerErrorMessagesHeading = getPlayerErrorsMapMessagesHeading();
  const code = Object.keys(playerErrorMessagesHeading).filter(error => errorCode && errorCode.includes(error));
  return playerErrorMessagesHeading[code];
}

/**
 * Gets the error message related to the code received from the caught error.
 * @param {String} message - caught error message.
 */
export function getPlayerErrorMessage(message) {
  if (!message || !message.includes) {
    return getPlayerDefaultErrorMessage();
  }
  const playerErrorMessages = getPlayerErrorMessages();
  const code = Object.keys(playerErrorMessages).filter(errorCode => message && message.includes(errorCode));
  Logger.log("Returning error message", "playerErrors.getPlayerErrorMessage");
  return playerErrorMessages[code] || getPlayerDefaultErrorMessage();
}

/**
 * Sends an error report on player errors.
 * @param {Object} errorData - Error object containing an error code and some extra data to be reported.
 */
export async function sendPlayerErrorReport(errorData = {}, message, bitmovinEvent, istokenCallFails, httpError) {
  const sdk = await getSdk();
  const { playerManager, reportsManager } = sdk;
  const { code, data } = errorData;
  const playerErrorMessages = getPlayerErrorMessages();

  if (!istokenCallFails) {
    await playerManager.sendPlaybackAnalyticsEvent(sdk.playerManager.analyticsEvents.error, {
      code: code,
      name: playerErrorMessages[code]
    });
  }

  const parameter = {
    errorCode: code,
    platform: PLATFORM,
    playbackInfo: {
      titleId: this.config.id,
      manifestUrl: this.config.manifest,
      releasePid: this.config.releasePids && this.config.releasePids[0],
      releaseUrl: this.config.releaseUrl,
      quality: this.config.playbackAnalytics.is4KEnabled,
      "x-request-id": this.xRequestId,
      playerErrorDump: {
        trace: (data && data.name) || message,
        placeInCode: (data && data.message) || message || "SDK - PlayerManager"
      }
    }
  };

  if (bitmovinEvent != null) {
    parameter.bitmovinError = bitmovinEvent;
  }

  if (httpError != null) {
    parameter.httpError = httpError;
  }

  await reportsManager.sendReport(
    { level: "error", event: "Playback", env: process.env.NODE_ENV },
    {
      eventId: "PlayerError",
      report: message,
      status: "error",
      parameter
    }
  );
}

/**
 * Sends an error report on player errors.
 * @param {Object} errorData - Error object containing an error code and some extra data to be reported.
 */
export async function sendPlayerErrorReportNative(errorData = {}) {
  const sdk = await getSdk();
  const { code, data, message } = errorData;
  await sdk.reportsManager.sendReport(
    { level: "error", event: "Playback", env: config.get().sdk.environment },
    {
      eventId: "PlayerError",
      report: code,
      status: "error",
      parameter: {
        errorCode: code,
        playbackInfo: {
          titleId: this.config.id,
          manifestUrl: this.config.manifest,
          releasePid: this.config.releasePids && this.config.releasePids[0],
          releaseUrl: this.config.releaseUrl,
          quality: this.config.playbackAnalytics.is4KEnabled,
          "x-request-id": this.xRequestId,
          playerErrorDump: {
            trace: (data && data.name) || message,
            placeInCode: (data && data.message) || message || "SDK - PlayerManager"
          }
        }
      }
    }
  );
}
